import React from 'react';
import styles from './styles.module.css';
import PropTypes from 'prop-types';
import { Table, Loading } from '../../../../../../common/ui';

const BottomContainer = ({ billData, isLoading, isError, error }) => {
  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch (err) {
      return dateString;
    }
  };

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return '0.00';
    const num = Number(value);
    if (isNaN(num)) return '0.00';
    return num.toFixed(2);
  };

  const toNumber = (value) => {
    const num = Number(value);
    return Number.isNaN(num) ? 0 : num;
  };

  if (isLoading) {
    return (
      <div className={styles.mainDiv}>
        <div className={styles.centerContent}>
          <Loading variant="inline" label="" />
          <p>Loading bill details...</p>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className={styles.mainDiv}>
        <div className={styles.centerContent}>
          <p className={styles.errorText}>Error loading bill details</p>
          <p className={styles.errorMessage}>{error || 'Something went wrong'}</p>
        </div>
      </div>
    );
  }

  if (!billData) {
    return (
      <div className={styles.mainDiv}>
        <div className={styles.centerContent}>
          <p>No bill data available</p>
        </div>
      </div>
    );
  }

  const { billHeader, billItems, summaryData, payments = [] } = billData;

  const getPaymentLabel = (payment) =>
    payment?.paymentMethodName || payment?.paymentMethodType || '-';

  const getPaymentAmount = (payment) => {
    const amount = toNumber(payment?.amount);
    return Math.abs(amount);
  };

  const paymentRows =
    payments.length > 0
      ? payments
      : billHeader.paidType
        ? [
            {
              paymentMethodName: billHeader.paidType,
              amount: summaryData.paidAmount,
            },
          ]
        : [];

  const columns = [
    { id: 'itemCode', label: 'Item Code', minWidth: 120 },
    { id: 'itemName', label: 'Name', minWidth: 170 },
    {
      id: 'unit_price',
      label: 'Price',
      minWidth: 100,
      format: (value) => formatCurrency(value),
    },
    {
      id: 'discount',
      label: 'Discount',
      minWidth: 100,
      format: (value) => formatCurrency(value),
    },
    { id: 'quantity', label: 'Quantity', minWidth: 100 },
    {
      id: 'total_amount',
      label: 'Total Amount',
      minWidth: 120,
      format: (value) => formatCurrency(value),
    },
    {
      id: 'cost',
      label: 'Cost',
      minWidth: 120,
      format: (value) => formatCurrency(value),
    },
    {
      id: 'profit',
      label: 'Profit',
      minWidth: 120,
      format: (value) => formatCurrency(value),
    },
    { id: 'returnQuantity', label: 'Return Quantity', minWidth: 120 },
  ];

  const rows =
    billItems && billItems.length > 0
      ? billItems.map((item, index) => ({
          id: index,
          itemCode: item.itemCode || '-',
          itemName: item.itemName || '-',
          unit_price: item.unit_price,
          discount: toNumber(item.discount),
          quantity: item.quantity || 0,
          total_amount: toNumber(item.total_amount),
          cost: toNumber(item.cost),
          returnQuantity: item.returnQuantity || 0,
          profit: toNumber(item.profit),
        }))
      : [];

  const totalCount = rows.length;
  const rowsPerPage = Math.max(10, totalCount);

  return (
    <div className={styles.mainDiv}>
      <div className={styles.headerCard}>
        <h2 className={styles.sectionTitle}>Bill Information</h2>
        <div className={styles.gridContainer}>
          <div className={styles.gridItem}>
            <p className={styles.fieldLabel}>Bill No</p>
            <p className={styles.fieldValue}>
              {billHeader.receiptNo || billHeader.billNo || '-'}
            </p>
          </div>
          <div className={styles.gridItem}>
            <p className={styles.fieldLabel}>Cashier</p>
            <p className={styles.fieldValue}>
              {billHeader.cashierName || '-'}
            </p>
          </div>
          <div className={styles.gridItem}>
            <p className={styles.fieldLabel}>Customer</p>
            <p className={styles.fieldValue}>
              {billHeader.customerName || '-'}
            </p>
          </div>
          <div className={styles.gridItem}>
            <p className={styles.fieldLabel}>Created At</p>
            <p className={styles.fieldValue}>
              {formatDate(billHeader.createdDate)}
            </p>
          </div>
          {billHeader.referenceNo && (
            <div className={styles.gridItem}>
              <p className={styles.fieldLabel}>Reference No</p>
              <p className={styles.fieldValue}>{billHeader.referenceNo}</p>
            </div>
          )}
        </div>
      </div>

      <div className={styles.summaryCards}>
        <div className={styles.summaryCard}>
          <p className={styles.cardLabel}>Total Amount</p>
          <h3 className={styles.cardValue}>
            LKR {formatCurrency(summaryData.totalAmount)}
          </h3>
        </div>
        <div className={styles.summaryCard}>
          <p className={styles.cardLabel}>Total Discount</p>
          <h3 className={styles.cardValue}>
            LKR {formatCurrency(summaryData.totalDiscount)}
          </h3>
        </div>
        <div className={styles.summaryCard}>
          <p className={styles.cardLabel}>Total Payable</p>
          <h3 className={styles.cardValue}>
            LKR {formatCurrency(summaryData.totalPayable)}
          </h3>
        </div>
        <div className={styles.summaryCard}>
          <p className={styles.cardLabel}>Paid Amount</p>
          <h3 className={styles.cardValue}>
            LKR {formatCurrency(summaryData.paidAmount)}
          </h3>
        </div>
      </div>

      <div className={styles.headerCard}>
        <h2 className={styles.sectionTitle}>Payment Methods</h2>
        {paymentRows.length > 0 ? (
          <div className={styles.gridContainer}>
            {paymentRows.map((payment, index) => (
              <div
                key={`${getPaymentLabel(payment)}-${index}`}
                className={styles.gridItem}
              >
                <p className={styles.fieldLabel}>{getPaymentLabel(payment)}</p>
                <p className={styles.fieldValue}>
                  LKR {formatCurrency(getPaymentAmount(payment))}
                </p>
              </div>
            ))}
          </div>
        ) : (
          <p className={styles.fieldValue}>-</p>
        )}
      </div>

      <div className={styles.tableSection}>
        <h2 className={styles.tableTitle}>Bill Items</h2>
        <div className={styles.tableWrap}>
          <Table
            columns={columns}
            rows={rows}
            page={0}
            rowsPerPage={rowsPerPage}
            totalCount={totalCount}
            disableInternalPagination
            onPageChange={() => {}}
            onRowsPerPageChange={() => {}}
          />
        </div>
      </div>
    </div>
  );
};

BottomContainer.propTypes = {
  billData: PropTypes.object,
  isLoading: PropTypes.bool,
  isError: PropTypes.bool,
  error: PropTypes.string,
};

export default BottomContainer;
